import {
  ChecksumAddress,
  DEFAULT_PROTOCOL_KEY,
  getChainKeyFromProtocol,
  LINKS,
  makeAddressSync,
  ProtocolKey,
  RUNTIME_PROTOCOL_INSTANCES,
} from '@silo/types';
import {
  MobileNav as BaseMobileNav,
  DiscordIcon,
  UnstyledLink,
  TelegramIcon,
  useMobileNavContext,
  Wallet,
  ConnectWalletBus,
} from '@silo/ui';
import {
  getWalletKeyFromSupportedWallet,
  SupportedWallet,
  useSiloWeb3,
  useWalletInfo,
  getWalletOptions,
} from '@silo/web3';
import { PATH } from 'app/hooks/useRoute';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useRef } from 'react';
import { connectWallet, mapTransactions } from './utils';
import { MobileRewardsTrigger } from './mobile-rewards-trigger';

export function MobileNav(): JSX.Element {
  const { protocolInstance, setProtocolInstance, account, activeWallet } = useSiloWeb3();
  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;

  return (
    <BaseMobileNav
      logoLink={
        <UnstyledLink href={PATH.DASHBOARD}>
          <BaseMobileNav.LogoLinkContent />
        </UnstyledLink>
      }
      protocolKey={protocolKey}
      setProtocolKey={setProtocolInstance}
      runtimeProtocolKeys={RUNTIME_PROTOCOL_INSTANCES}
      primaryLinks={
        <BaseMobileNav.NavLinkContainer>
          <MobileNavLink path={PATH.DASHBOARD}>Dashboard</MobileNavLink>
        </BaseMobileNav.NavLinkContainer>
      }
      secondaryLinks={
        <BaseMobileNav.NavLinkContainer>
          <MobileNavLink path={LINKS.DISCORD} external>
            <DiscordIcon />
            Discord
          </MobileNavLink>

          <MobileNavLink path={LINKS.TELEGRAM} external>
            <TelegramIcon />
            Telegram
          </MobileNavLink>
        </BaseMobileNav.NavLinkContainer>
      }
      walletTrigger={
        account && activeWallet ? (
          <ConnectedWalletTrigger protocolKey={protocolKey} account={account} wallet={activeWallet} />
        ) : (
          <DisconnectedWalletTrigger />
        )
      }
      walletFlow={
        account && activeWallet ? (
          <ConnectedWalletFlow protocolKey={protocolKey} account={account} wallet={activeWallet} />
        ) : (
          <DisconnectedWalletFlow />
        )
      }
      rewardsTrigger={<MobileRewardsTrigger protocolKey={protocolKey} />}
      contextSlot={<ContextSlot />}
    />
  );
}

interface MobileNavLinkProps {
  path: string;
  external?: boolean;
  children?: ReactNode;
}

function MobileNavLink({ path, children, external }: MobileNavLinkProps): JSX.Element {
  const { closeDialog } = useMobileNavContext();
  const router = useRouter();
  const isActive = path === router.route;

  return (
    <UnstyledLink onClick={closeDialog} className={BaseMobileNav.navLink({ isActive })} href={path} external={external}>
      {children}
    </UnstyledLink>
  );
}

function DisconnectedWalletTrigger(): JSX.Element {
  const { openWalletFlow } = useMobileNavContext();

  return <Wallet.DisconnectedTrigger onClick={openWalletFlow} className={BaseMobileNav.disconnectedTrigger()} />;
}

interface ConnectedWalletTriggerProps {
  protocolKey: ProtocolKey;
  account: ChecksumAddress;
  wallet: SupportedWallet;
}

function ConnectedWalletTrigger({
  account: accountLegacy,
  wallet,
}: ConnectedWalletTriggerProps): JSX.Element {
  const { openWalletFlow } = useMobileNavContext();
  const siloWeb3 = useSiloWeb3();

  const ensName = siloWeb3.ensName.data ?? undefined;
  const { isImpersonating } = siloWeb3;

  const account = makeAddressSync(accountLegacy);
  const { walletInfoState } = useWalletInfo();
  const walletKey = getWalletKeyFromSupportedWallet(wallet);

  return (
    <Wallet.ConnectedTrigger
      account={account}
      state={walletInfoState}
      ensName={ensName}
      isImpersonating={isImpersonating}
      onClick={openWalletFlow}
      walletKey={walletKey}
      chevron={<BaseMobileNav.Chevron />}
    />
  );
}

function DisconnectedWalletFlow(): JSX.Element {
  const walletOptions = getWalletOptions();

  return <Wallet.DisconnectedFlow connectWallet={connectWallet} walletOptions={walletOptions} />;
}

interface ConnectedWalletFlowProps {
  protocolKey: ProtocolKey;
  account: ChecksumAddress;
  wallet: SupportedWallet;
}

function ConnectedWalletFlow({
  protocolKey,
  account: accountLegacy,
  wallet,
}: ConnectedWalletFlowProps): JSX.Element {
  const { closeDialog } = useMobileNavContext();
  const siloWeb3 = useSiloWeb3();

  const ensName = siloWeb3.ensName.data ?? undefined;
  const { isImpersonating, disconnect } = siloWeb3;

  const account = makeAddressSync(accountLegacy);
  const activeWalletKey = getWalletKeyFromSupportedWallet(wallet);
  const chainKey = getChainKeyFromProtocol(protocolKey);
  const walletInfo = useWalletInfo();
  const { clearTransactions } = walletInfo;
  const recentTransactions = mapTransactions(walletInfo.recentTransactions);
  const walletOptions = getWalletOptions();

  return (
    <Wallet.ConnectedFlow
      isImpersonating={isImpersonating}
      account={account}
      ensName={ensName}
      disconnect={disconnect}
      activeWalletKey={activeWalletKey}
      chainKey={chainKey}
      recentTransactions={recentTransactions}
      clearTransactions={clearTransactions}
      walletOptions={walletOptions}
      connectWallet={connectWallet}
      onConnect={closeDialog}
    />
  );
}

function ContextSlot(): null {
  const { openWalletFlow } = useMobileNavContext();

  const ref = useRef<() => void>();

  ref.current = openWalletFlow;

  useEffect(() => {
    const unsubscribe = ConnectWalletBus.subscribe(() => {
      ref.current?.();
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return null;
}
